import React, { useEffect, useMemo, useState, useRef } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { LineChart, Instagram, TrendingUp, TrendingDown, Minus, Plus, Trash2, ExternalLink, Share2, Camera, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { IG_METRICS, TT_METRICS } from '../lib/insightMetrics';
import { copyToClipboard } from '../lib/clipboard';

// -----------------------------------------------------------------------------
// Metric schemas — the exact set a social media manager tracks monthly.
// `label` shown on tile, `hint` under it, `unit` for display suffix ('%'/'k'/'').
// `derived` marked = live calculation, not user-entered.
// Defined in `../lib/insightMetrics` so the public share page can reuse the same labels.
// -----------------------------------------------------------------------------

const METRIC_SETS = {
  organic: { instagram: IG_METRICS, tiktok: TT_METRICS },
};

const MODES = [
  { id: 'organic', label: 'organic social', emoji: '🌱' },
  { id: 'influencer', label: 'mailer performance', emoji: '🎁' },
];

const PLATFORMS = [
  { id: 'instagram', label: 'instagram', icon: Instagram, brand: 'from-fuchsia-500 to-orange-400' },
  { id: 'tiktok', label: 'tiktok', icon: TikTokIcon, brand: 'from-slate-900 to-slate-600' },
];

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 6.8a5.6 5.6 0 0 1-3.3-1.1V15a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .9.1v2.6a2.8 2.8 0 1 0 2 2.7V2h2.5a3.4 3.4 0 0 0 3.3 3v1.8Z"/>
    </svg>
  );
}

const currentMonthISO = () => new Date().toISOString().slice(0, 7);
const nfmt = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 }));

// -----------------------------------------------------------------------------
const Insights = () => {
  const [clients, setClients] = useState([]);
  const [mode, setMode] = useState('organic');           // 'organic' | 'influencer'
  const [selectedClientId, setSelectedClientId] = useState('');
  const [month, setMonth] = useState(currentMonthISO());
  const [snapshots, setSnapshots] = useState([]);        // all months for selected client + mode
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/clients');
        setClients(data || []);
      } catch { toast.error('failed to load clients'); }
      finally { setLoading(false); }
    })();
  }, []);

  // Filter clients by the ticked `insight_types` on the client itself
  const eligibleClients = useMemo(() => {
    return clients.filter((c) => (c.insight_types || []).includes(mode));
  }, [clients, mode]);

  // Whenever the mode changes or the eligible list updates, ensure selection stays valid
  useEffect(() => {
    if (!eligibleClients.length) { setSelectedClientId(''); return; }
    if (!eligibleClients.some((c) => c.id === selectedClientId)) setSelectedClientId(eligibleClients[0].id);
  }, [eligibleClients, selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) { setSnapshots([]); return; }
    api.get('/insights', { params: { client_id: selectedClientId, report_type: mode } })
      .then(({ data }) => setSnapshots(data || []))
      .catch(() => toast.error('failed to load insights'));
  }, [selectedClientId, mode]);

  const monthOptions = useMemo(() => {
    const set = new Set([currentMonthISO(), month]);
    snapshots.forEach((s) => set.add(s.month));
    const y = new Date().getFullYear();
    for (let m = 1; m <= 12; m += 1) set.add(`${y}-${String(m).padStart(2, '0')}`);
    return Array.from(set).sort().reverse();
  }, [snapshots, month]);

  const upsert = async (platform, patch) => {
    if (!selectedClientId) return;
    try {
      const { data } = await api.put('/insights', {
        client_id: selectedClientId, month, platform, report_type: mode, ...patch,
      });
      setSnapshots((prev) => {
        const others = prev.filter((s) => !(s.month === data.month && s.platform === data.platform && s.report_type === data.report_type));
        return [data, ...others];
      });
    } catch { toast.error('save failed'); }
  };

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const igSnap = snapshots.find((s) => s.month === month && s.platform === 'instagram');
  const ttSnap = snapshots.find((s) => s.month === month && s.platform === 'tiktok');
  const igPrev = prevMonthSnap(snapshots, month, 'instagram');
  const ttPrev = prevMonthSnap(snapshots, month, 'tiktok');
  const metricSet = METRIC_SETS[mode];

  const [copying, setCopying] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('all');
  useEffect(() => { setSelectedCampaignId('all'); }, [selectedClientId, mode]);

  const copyShareLink = async () => {
    if (!selectedClientId) return;
    setCopying(true);
    try {
      const { data } = await api.post(`/clients/${selectedClientId}/insights-share`);
      const params = new URLSearchParams({ mode });
      if (mode === 'organic') params.set('month', month);
      if (mode === 'influencer' && selectedCampaignId !== 'all') params.set('campaign_id', selectedCampaignId);
      const url = `${window.location.origin}/share/insights/${data.token}?${params.toString()}`;
      const copied = await copyToClipboard(url);
      if (copied) toast.success('view-only link copied — no login needed');
      else toast.success('view-only link ready', { description: url, duration: 15000 });
    } catch { toast.error('failed to create share link'); }
    finally { setCopying(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <LineChart className="w-6 h-6 text-pink-500" /> insights
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {mode === 'organic' ? 'monthly social media snapshots per client. instagram + tiktok, manual entry.' : 'live performance pulled from mailer campaigns per client.'}
          </p>
        </div>
        <div className="inline-flex bg-slate-100 rounded-full p-0.5" data-testid="insights-mode-toggle">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`h-9 px-4 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition ${mode === m.id ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`insights-mode-${m.id}`}
            >
              <span>{m.emoji}</span>{m.label}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : eligibleClients.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">
          no clients ticked for {MODES.find((m) => m.id === mode).emoji} {MODES.find((m) => m.id === mode).label} yet — open a client and tick <span className="italic font-semibold text-slate-600">insights reporting → {MODES.find((m) => m.id === mode).label}</span>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          {/* Client list */}
          <aside className="bg-white border border-slate-200 rounded-2xl p-2 h-fit sticky top-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 px-3 pt-2 pb-1">clients · {mode}</div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {eligibleClients.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedClientId(c.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${c.id === selectedClientId ? 'bg-pink-50 text-pink-700' : 'hover:bg-slate-50 text-slate-700'}`}
                    data-testid={`insights-client-${c.id}`}
                  >
                    <span className="text-base leading-none shrink-0">{c.logo || '📦'}</span>
                    <span className="truncate">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-lg">{selectedClient?.logo || '📦'}</span>
                <div>
                  <div className="text-sm font-semibold text-slate-800">{selectedClient?.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {MODES.find((m) => m.id === mode).emoji} {MODES.find((m) => m.id === mode).label}
                    {mode === 'organic' && <> · {new Date(month + '-01').toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</>}
                  </div>
                </div>
              </div>
              <div className="ml-auto inline-flex items-center gap-2">
                {mode === 'organic' && (
                  <>
                    <label className="text-[10px] uppercase tracking-widest text-slate-400">month</label>
                    <select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white"
                      data-testid="insights-month-select"
                    >
                      {monthOptions.map((m) => {
                        const [y, mm] = m.split('-').map(Number);
                        return <option key={m} value={m}>{new Date(y, mm - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</option>;
                      })}
                    </select>
                  </>
                )}
                {selectedClientId && (
                  <Button onClick={copyShareLink} disabled={copying} className="h-9 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="copy-insights-share-link-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1" /> copy view-only link
                  </Button>
                )}
              </div>
            </div>

            {mode === 'organic' ? (
              <>
                <PlatformPanel
                  platform="instagram"
                  metrics={metricSet.instagram}
                  snap={igSnap}
                  prev={igPrev}
                  onSave={(patch) => upsert('instagram', patch)}
                />
                <PlatformPanel
                  platform="tiktok"
                  metrics={metricSet.tiktok}
                  snap={ttSnap}
                  prev={ttPrev}
                  onSave={(patch) => upsert('tiktok', patch)}
                />
              </>
            ) : (
              <MailerPerformancePanel clientId={selectedClientId} campaignId={selectedCampaignId} onCampaignChange={setSelectedCampaignId} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const prevMonthSnap = (all, month, platform) => {
  // ISO month string sort works. Find the newest month for this platform < selected.
  const rows = all.filter((s) => s.platform === platform && s.month < month).sort((a, b) => b.month.localeCompare(a.month));
  return rows[0];
};

// -----------------------------------------------------------------------------
const PlatformPanel = ({ platform, metrics, snap, prev, onSave }) => {
  const meta = PLATFORMS.find((p) => p.id === platform);
  const Icon = meta.icon;
  const [metricsState, setMetricsState] = useState(snap?.metrics || {});
  const [posts, setPosts] = useState(snap?.top_posts || []);
  const [notes, setNotes] = useState(snap?.notes || '');
  const [extracting, setExtracting] = useState(false);
  const screenshotInputRef = useRef(null);

  useEffect(() => { setMetricsState(snap?.metrics || {}); }, [snap?.id, snap?.metrics]);
  useEffect(() => { setPosts(snap?.top_posts || []); }, [snap?.id, snap?.top_posts]);
  useEffect(() => { setNotes(snap?.notes || ''); }, [snap?.id, snap?.notes]);

  const commitMetric = (key, raw) => {
    const next = { ...metricsState };
    if (raw === '' || raw == null) delete next[key]; else next[key] = Number(raw);
    setMetricsState(next);
    if (next[key] !== snap?.metrics?.[key]) onSave({ metrics: next });
  };

  const handleScreenshot = async (file) => {
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('platform', platform);
      fd.append('file', file);
      const { data } = await api.post('/insights/extract-screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const extracted = data.metrics || {};
      const filled = Object.keys(extracted).length;
      if (!filled) { toast.error("couldn't read any metrics from that screenshot"); return; }
      const next = { ...metricsState, ...extracted };
      setMetricsState(next);
      onSave({ metrics: next });
      toast.success(`filled in ${filled} field${filled > 1 ? 's' : ''} from the screenshot`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "couldn't read that screenshot");
    } finally {
      setExtracting(false);
    }
  };

  const commitPosts = (nextPosts) => {
    setPosts(nextPosts);
    onSave({ top_posts: nextPosts });
  };

  const commitNotes = () => {
    if (notes === (snap?.notes || '')) return;
    onSave({ notes });
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <header className={`px-5 py-3 flex items-center gap-3 text-white bg-gradient-to-r ${meta.brand}`}>
        <Icon className="w-5 h-5" />
        <h2 className="text-sm font-semibold capitalize tracking-wide">{platform}</h2>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => screenshotInputRef.current?.click()}
            disabled={extracting}
            className="h-7 px-2.5 rounded-full text-[11px] font-semibold bg-white/15 hover:bg-white/25 text-white inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0"
            data-testid={`insights-upload-screenshot-${platform}`}
          >
            {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            {extracting ? 'reading…' : 'upload screenshot'}
          </button>
          <input
            ref={screenshotInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleScreenshot(f); }}
            data-testid={`insights-screenshot-input-${platform}`}
          />
          <span className="text-[10px] uppercase tracking-widest opacity-80 text-right leading-tight">
            {snap?.history?.length ? (
              <>
                {snap.history.length} {snap.history.length === 1 ? 'entry' : 'entries'}
                <br />
                last: {new Date(snap.updated_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </>
            ) : 'no data yet — start typing'}
          </span>
        </div>
      </header>

      <div className="p-5 space-y-5">
        {/* Metric tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {metrics.map((m) => (
            <MetricTile
              key={m.key}
              def={m}
              value={metricsState[m.key]}
              prevValue={prev?.metrics?.[m.key]}
              onCommit={(v) => commitMetric(m.key, v)}
            />
          ))}
        </div>

        {/* Top posts */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">top posts / videos</div>
            <button
              type="button"
              onClick={() => commitPosts([...posts, { id: 'p_' + Math.random().toString(36).slice(2, 8), link: '', views: null, likes: null, comments: null, shares: null, saves: null, note: '' }])}
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-pink-600 hover:text-pink-700"
              data-testid={`add-post-${platform}`}
            ><Plus className="w-3 h-3" /> add</button>
          </div>
          {posts.length === 0 ? (
            <div className="text-xs text-slate-400 italic px-1">no top posts recorded for this month yet</div>
          ) : (
            <ul className="space-y-2">
              {posts.map((p, idx) => (
                <PostRow
                  key={p.id}
                  post={p}
                  platform={platform}
                  onChange={(next) => { const arr = [...posts]; arr[idx] = next; commitPosts(arr); }}
                  onRemove={() => commitPosts(posts.filter((_, i) => i !== idx))}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">notes / commentary</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={commitNotes}
            placeholder="what performed, what didn't, next month's focus…"
            rows={3}
            className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-pink-200"
            data-testid={`notes-${platform}`}
          />
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------
const MetricTile = ({ def, value, prevValue, onCommit }) => {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);

  const diff = value != null && prevValue != null ? value - prevValue : null;
  const diffPct = diff != null && prevValue ? (diff / prevValue) * 100 : null;
  const Arrow = diff == null ? Minus : diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const arrowClass = diff == null || diff === 0 ? 'text-slate-400' : diff > 0 ? 'text-emerald-600' : 'text-rose-600';

  return (
    <label className="block bg-slate-50/50 hover:bg-white border border-slate-100 hover:border-pink-200 rounded-xl px-3 py-2.5 transition group cursor-text" data-testid={`metric-${def.key}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium truncate">{def.label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <input
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommit(draft)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          placeholder="—"
          className="flex-1 min-w-0 bg-transparent text-xl font-semibold tabular-nums text-slate-900 outline-none placeholder:text-slate-300"
        />
        {def.unit && <span className="text-xs text-slate-400 font-medium">{def.unit}</span>}
      </div>
      <div className="mt-1 flex items-center gap-1 text-[10px]">
        {diff != null ? (
          <span className={`inline-flex items-center gap-0.5 ${arrowClass}`}>
            <Arrow className="w-3 h-3" />
            <span className="font-semibold tabular-nums">
              {diff > 0 ? '+' : ''}{nfmt(diff)}{diffPct != null && Math.abs(diffPct) < 1000 ? ` · ${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(1)}%` : ''}
            </span>
          </span>
        ) : (
          <span className="text-slate-400">{def.hint || ' '}</span>
        )}
      </div>
    </label>
  );
};

// -----------------------------------------------------------------------------
const PostRow = ({ post, platform, onChange, onRemove }) => {
  const set = (patch) => onChange({ ...post, ...patch });
  const numFields = platform === 'instagram'
    ? [{ k: 'views', l: 'views' }, { k: 'likes', l: 'likes' }, { k: 'comments', l: 'comments' }, { k: 'saves', l: 'saves' }, { k: 'shares', l: 'shares' }]
    : [{ k: 'views', l: 'views' }, { k: 'likes', l: 'likes' }, { k: 'comments', l: 'comments' }, { k: 'shares', l: 'shares' }];
  return (
    <li className="border border-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <Input
          value={post.link || ''}
          onChange={(e) => set({ link: e.target.value })}
          onBlur={() => onChange(post)}
          placeholder="post URL"
          className="h-8 text-xs flex-1 min-w-0"
        />
        {post.link && (
          <a href={/^https?:\/\//i.test(post.link) ? post.link : `https://${post.link}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-600 shrink-0" title="open post">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {numFields.map((f) => (
          <label key={f.k} className="inline-flex flex-col items-start bg-slate-50 rounded-md px-2 py-1 border border-slate-100">
            <span className="text-[9px] uppercase tracking-widest text-slate-500">{f.l}</span>
            <input
              type="number"
              step="any"
              value={post[f.k] == null ? '' : post[f.k]}
              onChange={(e) => set({ [f.k]: e.target.value === '' ? null : Number(e.target.value) })}
              onBlur={() => onChange(post)}
              className="w-16 text-xs tabular-nums bg-transparent outline-none"
              placeholder="—"
            />
          </label>
        ))}
      </div>
      <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-600 shrink-0" title="remove"><Trash2 className="w-3.5 h-3.5" /></button>
      <Input
        value={post.note || ''}
        onChange={(e) => set({ note: e.target.value })}
        onBlur={() => onChange(post)}
        placeholder="note (optional)"
        className="h-8 text-xs md:col-span-3"
      />
    </li>
  );
};

// -----------------------------------------------------------------------------
// MailerPerformancePanel — replaces manual "influencer package" entry. Pulls stats
// live from every mailer campaign filed under this client, and can generate a
// view-only, no-login share link for the same report.
const MailerPerformancePanel = ({ clientId, campaignId, onCampaignChange }) => {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('likes');
  const requestedClientId = useRef(null);
  const requestedCampaignsClientId = useRef(null);

  useEffect(() => {
    if (!clientId) { setCampaigns([]); return; }
    requestedCampaignsClientId.current = clientId;
    api.get('/mailers/campaigns', { params: { client_id: clientId } })
      .then(({ data }) => { if (requestedCampaignsClientId.current === clientId) setCampaigns(data || []); })
      .catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (!clientId) { setStats(null); return; }
    const key = `${clientId}:${campaignId}`;
    requestedClientId.current = key;
    setLoading(true);
    const params = { client_id: clientId };
    if (campaignId && campaignId !== 'all') params.campaign_id = campaignId;
    api.get('/mailers/insights', { params })
      .then(({ data }) => { if (requestedClientId.current === key) setStats(data); })
      .catch(() => { if (requestedClientId.current === key) toast.error('failed to load mailer performance'); })
      .finally(() => { if (requestedClientId.current === key) setLoading(false); });
  }, [clientId, campaignId]);

  if (loading) return <div className="py-10 text-center text-sm text-slate-400">loading…</div>;
  if (!stats) return null;

  const hasGmv = stats.has_affiliate;
  const sortedContent = [...stats.content].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  const SORT_OPTIONS = [
    { key: 'likes', label: 'likes' },
    { key: 'views', label: 'views' },
    ...(hasGmv ? [{ key: 'gmv', label: 'gmv' }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">pulled live from mailers — {stats.campaigns_count} campaign{stats.campaigns_count === 1 ? '' : 's'}</p>
        {campaigns.length > 0 && (
          <div className="inline-flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">mailer</label>
            <select
              value={campaignId}
              onChange={(e) => onCampaignChange(e.target.value)}
              className="h-9 text-xs border border-pink-200 rounded-md px-2 bg-white"
              data-testid="insights-mailer-campaign-select"
            >
              <option value="all">all mailers</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MailerStatTile label="influencers sent" value={nfmt(stats.total_creators)} />
        <MailerStatTile label="influencers posted" value={nfmt(stats.posted_count)} />
        <MailerStatTile label="% posted" value={`${nfmt(stats.pct_posted)}%`} highlight />
        {hasGmv && <MailerStatTile label="total gmv" value={`£${nfmt(stats.total_gmv)}`} />}
        <MailerStatTile label="total ig follower reach" value={nfmt(stats.total_ig_followers)} />
        <MailerStatTile label="total tt follower reach" value={nfmt(stats.total_tt_followers)} />
        <MailerStatTile label="total likes" value={nfmt(stats.total_likes)} />
        <MailerStatTile label="total views" value={nfmt(stats.total_views)} />
      </div>
      <div className="bg-white border border-pink-100 rounded-2xl overflow-hidden" data-testid="mailer-content-leaderboard">
        <div className="px-4 py-2.5 bg-pink-50 border-b border-pink-100 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[11px] font-semibold text-pink-700 uppercase tracking-widest">content</span>
          <div className="inline-flex items-center gap-1" data-testid="mailer-content-sort">
            <span className="text-[10px] uppercase tracking-widest text-pink-400 mr-1">sort by</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortKey(opt.key)}
                className={`text-[11px] px-2 py-1 rounded-full transition-colors ${sortKey === opt.key ? 'bg-pink-600 text-white' : 'bg-white text-pink-600 hover:bg-pink-100'}`}
                data-testid={`mailer-content-sort-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {stats.content.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">no content posted yet — add creators under mailers</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sortedContent.map((c, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="text-slate-400 w-5 text-right text-xs">{i + 1}</span>
                <span className="capitalize text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.platform}</span>
                <span className="flex-1 min-w-0 truncate text-slate-700 text-xs">{c.creator}</span>
                <span className="text-slate-500 text-xs">{nfmt(c.likes)} likes</span>
                <span className="text-slate-500 text-xs">{nfmt(c.views)} views</span>
                {c.gmv != null && <span className="text-slate-500 text-xs">£{nfmt(c.gmv)} gmv</span>}
                <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:text-pink-700"><ExternalLink className="w-3.5 h-3.5" /></a>
                {c.notes && <span className="text-slate-400 text-xs italic truncate max-w-[160px]" data-testid={`mailer-content-note-${i}`}>"{c.notes}"</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const MailerStatTile = ({ label, value, highlight }) => (
  <div className={`border rounded-xl px-3 py-2.5 ${highlight ? 'bg-pink-50 border-pink-200' : 'bg-white border-slate-200'}`}>
    <div className={`text-[10px] uppercase tracking-widest font-medium ${highlight ? 'text-pink-600' : 'text-slate-500'}`}>{label}</div>
    <div className={`mt-0.5 text-xl font-semibold ${highlight ? 'text-pink-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

export default Insights;
